/**
 * UcLedger — блок «Леджер (N)» UC-панели (screen-22, эталон renderLed
 * js:160-179): строка — ±сумма (pos/neg/zero), тип-чип (словарь TCH,
 * sticker_payout → «выплата»; legacy-типы A26 — сырым именем приглушённо,
 * УД11 — только отображаются), имя первым словом, описание ellipsis, время
 * DD.MM HH:MM (UTC+5). Фильтр-чип оператора — Dropdown с «Все» + filter;
 * при выборе леджер приходит серверной ручкой /api/tg/ledger?operator_id
 * (УД5). «Показать ещё (N)» — клиентское окно 12/+10 поверх растущего
 * limit /api/uc/log. Flash первой строки — после успешного бонуса/штрафа.
 */
import { useState, type MouseEvent } from "react";
import { Dropdown } from "../../components/shared";
import type { UcAdmin } from "../../hooks/useUcAdmin";
import type { UcLedgerKnownType } from "../../api/ucTypes";
import { TCH, firstWord, fmtTm, fmtUC } from "./ucFmt";
import cfg from "./config.module.css";
import dcss from "./dicts.module.css";
import pcss from "./prices.module.css";
import qcss from "./quests.module.css";
import ucss from "./uc.module.css";

/** Строки-лог шапки (эталон html:223). */
const LedIco = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 5h16M4 10h16M4 15h10M4 20h7" />
  </svg>
);

const START = 12;
const STEP = 10;

/** Лейбл тип-чипа: известный тип — из TCH, legacy/неизвестный — сырым (УД11). */
const tchLabel = (t: string): string | undefined =>
  Object.prototype.hasOwnProperty.call(TCH, t) ? TCH[t as UcLedgerKnownType] : undefined;

export function UcLedger({ uc, onTip }: {
  uc: UcAdmin;
  onTip: (e: MouseEvent<HTMLButtonElement>) => void;
}) {
  const [dd, setDd] = useState<HTMLElement | null>(null);
  const [shown, setShown] = useState(START);

  const rows = uc.ledger;
  const rest = rows.length - shown;
  const flash = uc.flash?.key === "led" ? uc.flash : null;

  const more = () => {
    setShown((s) => s + STEP);
    /* Буфер лога кончается — дозапросить у сервера следующую сотню (УД5). */
    if (uc.hasMoreLog && rows.length - (shown + STEP) < STEP) uc.moreLog();
  };

  return (
    <div className={ucss.blk}>
      <div className={`${qcss.s17head} ${ucss.hd}`}>
        <span className={ucss.hcy}><LedIco /></span>
        <h2 className={`${cfg.secT} ${cfg.cy}`}>Леджер ({rows.length})</h2>
        <button type="button" className={pcss.qmark} data-qmark="" aria-label="Справка" onClick={onTip}>
          <span>?</span>
        </button>
        <button
          type="button"
          className={`${dcss.fchip} ${ucss.chipW} ${uc.ledOp ? dcss.on : ""}`}
          onClick={(e) => setDd(e.currentTarget)}
        >
          <span className={dcss.fv}>{uc.ledOp ? firstWord(uc.nameOf(uc.ledOp)) : "Все"}</span>
          <span className={dcss.chev}>▾</span>
        </button>
      </div>
      {rows.length ? (
        <div className={ucss.mcard}>
          {rows.slice(0, shown).map((r, i) => {
            const lab = tchLabel(r.type);
            const fl = flash && i === 0;
            return (
              <div
                key={fl ? `${r.id}:${flash.n}` : r.id}
                className={`${ucss.lrow} ${fl ? cfg.flash : ""}`}
              >
                <span className={`${ucss.lamt} ${qcss.num} ${r.amount > 0 ? ucss.pos : r.amount < 0 ? ucss.neg : ucss.zero}`}>
                  {r.amount > 0 ? "+" : r.amount < 0 ? "−" : ""}{fmtUC(r.amount)}
                </span>
                <span className={`${ucss.tch} ${(lab ? ucss[r.type] : undefined) ?? ucss.legacy ?? ""}`}>
                  {lab ?? r.type}
                </span>
                <span className={ucss.ltxt}>
                  <span className={ucss.lname}>{firstWord(r.operator_name)}</span>
                  <span className={ucss.ldesc}>{r.description ?? ""}</span>
                </span>
                <span className={`${ucss.ltm} ${qcss.num}`}>{fmtTm(r.created_at)}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={cfg.teaEmpty}>Нет транзакций</div>
      )}
      {rest > 0 || uc.hasMoreLog ? (
        <button type="button" className={ucss.morebtn} onClick={more}>
          {rest > 0 ? `Показать ещё (${rest})` : "Показать ещё"}
        </button>
      ) : null}
      <Dropdown
        open={!!dd}
        anchor={dd}
        multi={false}
        filter
        selectedIds={uc.ledOp ? [uc.ledOp] : ["__all"]}
        items={[{ id: "__all", label: "Все" }, ...uc.balances.map((b) => ({ id: b.operator_id, label: b.operator_name }))]}
        onClose={() => setDd(null)}
        onCommit={(ids) => {
          const id = ids[0];
          if (id == null) return;
          uc.setLedOp(id === "__all" ? null : String(id));
          setShown(START);
        }}
      />
    </div>
  );
}
